import { Ability, AbilityMarshaller } from 'app/constants/enums/Ability';
import { IUserExternalAuth, UserExternalAuth } from 'app/constants/schemas/UserExternalAuth';
import vmClient from '../../services/vmClient';
import {
  Entity,
  EntityStringId,
  IEntity,
  IEntityType,
} from 'app/types/entity';
import { DateMarshaller } from './Date';
import { IStaffProfile, StaffProfile } from './StaffProfile';
import { ITeacherProfile, TeacherProfile } from './TeacherProfile';
import { Language, LanguageMarshaller } from 'app/constants/enums/Language';
import { Timezone, TimezoneMarshaller } from 'app/constants/schemas/Timezone';
import { BrandReference, IBrandReference } from './BrandReference';
import { PasswordType, PasswordTypeMarshaller } from '../enums/PasswordType';

export interface IUser extends IEntity {
  attributes: {
    id: EntityStringId;
    displayName: string | null;
    password?: string;
    name: string | null;
    gender: string | null;
    image: string | null;
    email: string | null;
    unconfirmedEmail: string | null;
    language: Language | null;
    timezone: Timezone | null;
    skypeId: string | null;
    profileCompleted: boolean | null;
    birthdate: Date | null;
    abilities: Ability[];
    confirmed: boolean;
    marketing_email_consent: boolean;
    hasPassword: boolean;
    deletedAt: Date | null;
    passwordType: PasswordType;
  };
  associations: {
    brand: IBrandReference;
    userExternalAuths: IUserExternalAuth[];
    teacher: ITeacherProfile | null;
    staffMember: IStaffProfile | null;
  };
}

export const userAttributes = {
  displayName: 'string',
  password:    {
    notNull:   true,
    type:      'string',
    writeOnly: true,
  },
  name:                    'string',
  gender:                  'string',
  image:                   'string',
  email:                   'string',
  unconfirmedEmail:        'string',
  skypeId:                 'string',
  profileCompleted:        'boolean',
  confirmed:               'boolean',
  marketing_email_consent: 'boolean',
  hasPassword:             'boolean',
  timezone: {
    type: Timezone,
    marshaller: TimezoneMarshaller,
  },
  language: {
    type: Language,
    marshaller: LanguageMarshaller,
  },
  birthdate:               {
    type:       Date,
    marshaller: DateMarshaller,
  },
  abilities: {
    collection: true,
    type:       Ability,
    marshaller: AbilityMarshaller,
  },
  deletedAt: {
    type:       Date,
    marshaller: DateMarshaller,
  },
  passwordType: {
    type: PasswordType,
    marshaller: PasswordTypeMarshaller,
    notNull: true,
  },
};

export const userAssociations = {
  userExternalAuths: {
    type: UserExternalAuth,
    collection: true,
  },
  staffMember: {
    type: StaffProfile,
    shared: true,
  },
  teacher: {
    type: TeacherProfile,
    shared: true,
  },
  brand: {
    type: BrandReference,
    notNull: true,
    shared: true,
  },
};

export type User = Entity<IUser>;
// defineClass is a class factory, normally classes create a runtime variable and identifier in the type namespace.
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const User: IEntityType<IUser> = vmClient.defineClass({
  typeName: 'User',
  version: 14,
  persisted: true,
  attributes: userAttributes,
  associations: userAssociations,
});

export default User;
